/**
 * Shopify Backend API client
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-13 18:36:59
 */

import axios, {type AxiosInstance, AxiosRequestConfig} from 'axios';

// helpers
import {getCurrentShop} from '~/helpers/shopify';
import {parseAxiosError} from '~/helpers/request';

// types
import type {GetQueryParams, PlainObject, PostPayload} from '~/types/api-client.types';
import type {HandleType} from '~/helpers/shopify';

/**
 * Url type for url normalization
 * @see {@link ApiClient#toUrl ApiClient.toUrl()} method for usage information
 */
export enum UrlType {
  Absolute = 0,
  Relative = 1,
  Base = 2,
}

/**
 * Shopify Backend API client
 * The update or fetch data from the backend is done via this client
 */
export default class ApiClient {
  /** @internal The Axios instance */
  private readonly apiClient: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {'X-Crm-Type': 'shopify', 'X-Type': 'embed'},
  });

  /**
   * Returns the current shop domain (e.g., `https://my-shop.myshopify.com`)
   */
  get shop(): string {
    return getCurrentShop();
  }

  /**
   * Get the Axios instance
   */
  getInstance(): AxiosInstance {
    return this.apiClient;
  }

  /**
   * Return the normalized absolute or relative url
   * @param route - The route to normalize
   * @param [query] - (optional) The query params to append at the end of url
   * @param [type] - (optional) The type of url to return
   * @returns - The constructed url
   */
  toUrl(route: string, query: PlainObject = {}, type: UrlType = UrlType.Relative): string {
    const params = Object.keys(query).length ? '?' + new URLSearchParams(query).toString() : '';
    const fixedRoute: string = '/' + route.replace(/^\//, '').replace(/\/$/, '') + params;

    const baseUrl = String(process.env?.REACT_APP_BACKEND_URL ?? '').replace(/\/$/, '');

    switch (type) {
      case UrlType.Base:
        return baseUrl;
      case UrlType.Relative:
        return fixedRoute;
      case UrlType.Absolute:
      default:
        return baseUrl + fixedRoute;
    }
  }

  /**
   * Sends a POST request to the shopify backend server
   * @param route - The route to send the request to (e.g., `'/api/templates'`)
   * @param [data] - The data to include in the request body
   * @param config - The Axios request config
   * @returns The response from the server
   * @throws {Error} - If the request fails due to an error
   */
  async post<
    TResponse extends any = any, TPayload extends PostPayload = PostPayload
  >(route: string,
    data: TPayload = {} as TPayload,
    config: AxiosRequestConfig<PostPayload<TPayload>> | undefined = undefined): Promise<TResponse> {
    try {
      return this.getInstance()
        .post<unknown, TResponse, PostPayload<TPayload>>(
          this.toUrl(route), {
            ...data,
            shop: this.shop,
          },
          config,
        );
    } catch (e) {
      console.log(`POST '${route}' Request failed`, e);
      throw new Error(parseAxiosError(e as PlainObject));
    }
  }

  /**
   * Sends a POST request with handle to the shopify backend server
   * @param route - The route to send the request to (e.g., `'/api/templates'`)
   * @param handle - Shopify handle name (e.g., `'send-postcard'`)
   * @param data - The data to include in the request body
   * @param config - The Axios request config
   * @returns The response from the server
   * @throws {Error} - If the request fails due to an error
   */
  async postWithHandle<
    TResponse extends any = any, TPayload extends PostPayload = PostPayload
  >(route: string, handle: HandleType,
    data: TPayload = {} as TPayload,
    config: AxiosRequestConfig<PostPayload<TPayload>> | undefined = undefined): Promise<TResponse> {
    try {
      return this.post(route, {...data, handle}, config);
    } catch (e) {
      console.log(`POST '${route}' Request failed`, e);
      throw new Error(parseAxiosError(e as PlainObject));
    }
  }

  /**
   * Sends a PUT request to the shopify backend server
   * @param route - The route to send the request to (e.g., `'/api/templates'`)
   * @param data - The data to include in the request body
   * @param config - The Axios request config
   * @returns The response from the server
   * @throws {Error} - If the request fails due to an error
   */
  async put<
    TResponse extends any = any, TPayload extends PostPayload = PostPayload
  >(route: string, data: TPayload = {} as TPayload,
    config: AxiosRequestConfig<PostPayload<TPayload>> | undefined = undefined): Promise<TResponse> {
    try {
      return this.getInstance()
        .put<unknown, TResponse, PostPayload<TPayload>>(
          this.toUrl(route), {
            ...data,
            shop: this.shop,
          },
          config,
        );
    } catch (e) {
      console.log(`PUT '${route}' Request failed`, e);
      throw new Error(parseAxiosError(e as PlainObject));
    }
  }

  /**
   * Sends a GET request to the shopify backend server
   * @param route - The route to send the request to
   * @param [query] - The query params to append as url query string
   * @returns The response from the server
   * @throws {Error} - If the request fails due to an error
   */
  async get<
    TResponse extends any = any, TPayload extends GetQueryParams = GetQueryParams
  >(route: string, query: TPayload = {} as TPayload): Promise<TResponse> {
    const queryParams = {
      ...query,
      shop: this.shop,
    } as GetQueryParams<TPayload>;

    try {
      return this.getInstance()
        .get<unknown, TResponse, GetQueryParams<TPayload>>(
          this.toUrl(route, queryParams),
        );
    } catch (e) {
      console.log(`GET '${route}' Request failed`, e);
      throw new Error(parseAxiosError(e as PlainObject));
    }
  }
}
